import React, { useState, useEffect, useContext, useRef, useCallback, memo, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Button,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import SearchIcon from '@mui/icons-material/Search';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person'; // <-- NEW import
import LayersIcon from '@mui/icons-material/Layers'; // New import for layer icon
import FilterListIcon from '@mui/icons-material/FilterList'; // <-- NEW import for Filter tab
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // <-- NEW import for Reviewed button
import NoteAddIcon from '@mui/icons-material/NoteAdd'; // <-- Nieuw: import voor notitie-icoon
import DrawIcon from '@mui/icons-material/Draw'; // <-- Nieuw: import voor intekentool
import OpenInNewIcon from '@mui/icons-material/OpenInNew'; // <-- Nieuw: import voor PDF openen in nieuwe tab
import DrawingTool from '../drawing/DrawingTool'; // <-- Import voor intekentool component
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import ProjectInfoTab from './ProjectInfoTab';
import DrillPointsTab from './DrillPointsTab';
// import DrillMasterTab from './DrillMasterTab'; // <-- Temporarily removed
import LayerInfo from './Layerinfo'; // New import
import { throttle } from 'lodash';
import pageCache from './pageCache';
import SampleTab from './Veldmonster_tab'; // Add this import
import AnalysemonsterTab from './Analysemonster_tab'; // Add this new import
import Analysis_tab from './Analysis_tab'; // Add this new import
import FilterTab from './Filter_tab'; // <-- NEW import for Filter tab
import './DocumentViewer.css'; // Importeer de CSS
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloudDoneIcon from '@mui/icons-material/CloudDone';

// Set rendering parameters for optimization
const RENDER_OPTIONS = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist@5.1.91/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@5.1.91/standard_fonts/',
  verbosity: 0,
};

// Cache rendered pages for better performance
const PAGE_BUFFER = 5; // Number of pages to render above/below viewport
const ZOOM_LEVELS = [0.5, 0.6, 0.7, 0.8, 9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.8, 2.0, 2.2, 2.5, 3.0]; // Predefined zoom levels for better caching

// NIEUWE customTextRenderer functie
const customTextRenderer = (textItem, searchQuery, isCurrentHighlight) => {
  // DEBUG: Wordt de renderer aangeroepen?
  // console.log('[DEBUG] CustomTextRenderer Input:', { textItemStr: textItem?.str, searchQuery, isCurrentHighlight });

  const str = textItem?.str;

  if (!searchQuery || !str) {
    return str; // Geef de originele string terug
  }

  // Escape de zoekterm voor gebruik in RegExp
  const escapedSearchQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearchQuery})`, 'gi');
  
  // Helper functie om HTML te escapen (voorkomt XSS als tekst '<' bevat)
  const escapeHtml = (unsafe) => {
    return unsafe
         .replace(/&/g, "&amp;")
         .replace(/</g, "&lt;")
         .replace(/>/g, "&gt;")
         .replace(/"/g, "&quot;")
         .replace(/'/g, "&#039;");
  }

  // Vervang de gevonden delen met <mark> tags in de string
  const resultString = str.replace(regex, (match) => {
    const className = `search-highlight ${isCurrentHighlight ? 'search-highlight--active' : ''}`;
    // Belangrijk: Escape de match inhoud voordat je het in de HTML tag zet
    return `<mark class="${className}">${escapeHtml(match)}</mark>`;
  });

  // Geef de volledige HTML string terug
  return resultString;
};

// Improved virtualized PDF page with zoom optimizations
const VirtualizedPDFPage = memo(({ 
  pageNumber, 
  scale, 
  onLoadSuccess,
  onDimensionsLoad,
  isVisible,
  pdfDocument,
  previousCachedPage,
  searchQuery, 
  currentSearchResult,
  isActiveSearchResult
}) => {
  const pageRef = useRef(null);
  const [, setIsLoaded] = useState(false);
  const renderTaskRef = useRef(null);
  const hasRenderedRef = useRef(false);

  // Clean up rendering tasks
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pageNumber, scale, isVisible]);

  // Handle page load success - geef nu originele dimensies door
  const handleLoadSuccess = useCallback((page) => {
    setIsLoaded(true);
    
    // Roep onLoadSuccess door voor algemeen gebruik (als parent dat nodig heeft)
    if (onLoadSuccess && !hasRenderedRef.current) {
      onLoadSuccess(page);
      hasRenderedRef.current = true;
    }
    
    // Roep de nieuwe callback aan met originele dimensies
    if (onDimensionsLoad) {
      onDimensionsLoad(pageNumber, { width: page.width, height: page.height });
    }

  }, [onLoadSuccess, onDimensionsLoad, pageNumber]);

  // Only render if the page is visible
  if (!isVisible) {
    // Placeholder: probeer hoogte te schatten of gebruik vaste fallback
    // We hebben geen pageSize meer, dus vaste fallback is nu nodig.
    const placeholderHeight = 800; // Of een andere redelijke schatting
    return (
      <div style={{ 
        height: placeholderHeight, 
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="page-placeholder" data-page={pageNumber}></div>
      </div>
    );
  }

  const isThisTheActiveHighlight = isActiveSearchResult && 
                                   currentSearchResult && 
                                   currentSearchResult.pageNumber === pageNumber;

  return (
    <div ref={pageRef} style={{ position: 'relative' }}>
      <Page
        key={`page-${pageNumber}-scale-${scale}-search-${searchQuery}`}
        pageNumber={pageNumber}
        scale={scale}
        renderAnnotationLayer={true}
        renderTextLayer={true} 
        onLoadSuccess={handleLoadSuccess}
        renderMode="svg"
        customTextRenderer={(textItem) => 
          customTextRenderer(textItem, searchQuery, isThisTheActiveHighlight)
        }
        loading={ 
          // Gebruik vaste hoogte voor loading, of probeer te schatten
          <div style={{ height: 800 }}></div> 
        }
      />
    </div>
  );
}, (prevProps, nextProps) => {
  if (!nextProps.isVisible && !prevProps.isVisible) return true;
  
  return (
    prevProps.pageNumber === nextProps.pageNumber &&
    prevProps.scale === nextProps.scale &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.searchQuery === nextProps.searchQuery &&
    prevProps.isActiveSearchResult === nextProps.isActiveSearchResult &&
    prevProps.currentSearchResult?.pageNumber === nextProps.currentSearchResult?.pageNumber &&
    prevProps.currentSearchResult?.index === nextProps.currentSearchResult?.index 
  );
});

// Advanced virtualized PDF container component
const VirtualizedPDF = memo(({ 
  numPages, 
  pdfDocument, 
  scale, 
  onPageLoad,
  onTextContent,
  onPageChange,
  searchQuery,
  currentSearchResult,
  onOriginalDimensionsUpdate
}) => {
  const [visibleRange, setVisibleRange] = useState({ startIndex: 0, endIndex: 5 });
  const [containerMeasurements, setContainerMeasurements] = useState({ height: 0, width: 0 });
  const [originalPageDimensions, setOriginalPageDimensions] = useState({});
  const containerRef = useRef(null);
  const pagesRef = useRef({});
  
  // Callback om ALLEEN interne state bij te werken
  const handleDimensionsLoad = useCallback((pageNumber, dimensions) => {
    setOriginalPageDimensions(prev => ({
      ...prev,
      [pageNumber]: dimensions
    }));
    // Verwijderd: Direct aanroepen van onOriginalDimensionsUpdate hier
  }, []); // Dependency onOriginalDimensionsUpdate verwijderd

  // Effect hook om parent te updaten NADAT interne state is bijgewerkt
  useEffect(() => {
    // Geef de volledige set door als deze verandert
    if (onOriginalDimensionsUpdate && Object.keys(originalPageDimensions).length > 0) {
        // Verwijderd: console.log('[VirtualizedPDF/useEffect] Updating parent...');
        onOriginalDimensionsUpdate(originalPageDimensions);
    }
  }, [originalPageDimensions, onOriginalDimensionsUpdate]); // Trigger op interne state change

  // useEffect voor ResizeObserver blijft
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setContainerMeasurements({ width, height });
      }
    });
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  // isPageVisible blijft
  const isPageVisible = useCallback((pageIndex) => {
    const pageNumber = pageIndex + 1;
    return pageNumber >= visibleRange.startIndex && pageNumber <= visibleRange.endIndex;
  }, [visibleRange]);
  
  const PAGE_MARGIN = 20;
  const ESTIMATED_PAGE_HEIGHT = 800; // Vaste fallback hoogte voor layout
  
  // handleScroll gebruikt nu een vaste schatting * scale
  const handleScroll = useCallback(throttle((info) => {
    const { scrollTop, clientHeight } = info;
    let accumulatedHeight = 0;
    let currentPage = 1;

    // Gebruik schatting * scale voor scroll logica
    const estimatedScaledHeight = ESTIMATED_PAGE_HEIGHT * scale;

    for (let i = 0; i < numPages; i++) { 
      // Gebruik de geschatte geschaalde hoogte
      const height = estimatedScaledHeight; 
      if (accumulatedHeight + height / 2 > scrollTop) {
          break;
      }
      currentPage = i + 1;
      accumulatedHeight += height + PAGE_MARGIN;
    }

    onPageChange?.(currentPage, {
      startIndex: Math.max(1, currentPage - PAGE_BUFFER),
      endIndex: Math.min(numPages, currentPage + PAGE_BUFFER)
    });
    
    const calculatedStart = Math.max(1, currentPage - PAGE_BUFFER);
    const calculatedEnd = Math.min(numPages, currentPage + PAGE_BUFFER);
    
    setVisibleRange({
      startIndex: calculatedStart,
      endIndex: calculatedEnd
    });
    // pageHeights niet meer nodig als dependency, wel scale
  }, 100), [numPages, onPageChange, scale]); 

  // useMemo voor totalHeight gebruikt nu schatting
  const totalHeight = useMemo(() => {
    const estimatedScaledHeight = ESTIMATED_PAGE_HEIGHT * scale;
    return numPages * (estimatedScaledHeight + PAGE_MARGIN); // Schatting
  }, [numPages, scale]);
  
  if (!pdfDocument) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>Loading document...</Typography>
      </Box>
    );
  }
  
  return (
    <div 
      ref={containerRef} 
      style={{ 
        height: '100%', 
        overflow: 'auto',
        position: 'relative',
        padding: '16px',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center' 
      }}
      onScroll={(e) => handleScroll({
        scrollTop: e.target.scrollTop,
        scrollHeight: totalHeight, // Gebruik geschatte totale hoogte
        clientHeight: e.target.clientHeight
      })}
    >
      {/* Gebruik geschatte totalHeight voor deze wrapper */}
      <div style={{ width: '100%', height: totalHeight }}> 
        {Array.from(new Array(numPages), (_, index) => {
          const pageNumber = index + 1;
          // Gebruik geschatte hoogte * scale voor minHeight
          const estimatedPageLayoutHeight = ESTIMATED_PAGE_HEIGHT * scale;
          
          return (
            <div
              key={`page-container-${pageNumber}`}
              ref={el => pagesRef.current[pageNumber] = el}
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: PAGE_MARGIN,
                minHeight: estimatedPageLayoutHeight, // Gebruik schatting voor layout
              }}
            >
              <div style={{
                boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                backgroundColor: 'white',
                borderRadius: '4px',
                position: 'relative'
                // Breedte/Hoogte wordt impliciet bepaald door VirtualizedPDFPage
              }}>
                <VirtualizedPDFPage
                  key={`page-render-${pageNumber}`} 
                  pageNumber={pageNumber}
                  scale={scale}
                  isVisible={isPageVisible(index)}
                  onLoadSuccess={(page) => onPageLoad?.(pageNumber, page)}
                  onDimensionsLoad={handleDimensionsLoad} // Doorgeven aan page
                  pdfDocument={pdfDocument}
                  searchQuery={searchQuery}
                  currentSearchResult={currentSearchResult}
                  isActiveSearchResult={currentSearchResult?.pageNumber === pageNumber}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Memoize the document page to prevent re-rendering during resize

// Memoized tabs component
const InfoTabs = memo(({ selectedTab, handleTabChange, document, isReadOnly, onOpenPdfInNewTab, onOpenDrawingTool }) => {
  return (
    <>
      {/* Action buttons boven de tabs */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 2, 
        py: 1,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500 }}>
          Document Info
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Open PDF in nieuwe tab">
            <IconButton size="small" onClick={onOpenPdfInNewTab}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Intekentool">
            <IconButton size="small" onClick={onOpenDrawingTool}>
              <DrawIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Tabs 
        value={selectedTab} 
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          minHeight: 48,
          bgcolor: 'background.paper'
        }}
      >
        <Tab 
          icon={<DescriptionIcon />} 
          iconPosition="start"
          label="Project Info" 
          sx={{ minHeight: 48 }}
        />
        <Tab 
          icon={<FolderIcon />} 
          iconPosition="start"
          label="Boringen" 
          sx={{ minHeight: 48 }}
        />
        <Tab 
          icon={<FilterListIcon />} // <-- NEW Filter Tab Icon
          iconPosition="start"
          label="Filters" 
          sx={{ minHeight: 48 }}
        />
        {/* <Tab 
          icon={<PersonIcon />} 
          iconPosition="start"
          label="Drillmaster"     
          sx={{ minHeight: 48 }}
        /> */}
        <Tab 
          icon={<LayersIcon />} 
          iconPosition="start"
          label="Lagen"     
          sx={{ minHeight: 48 }}
        />
        <Tab 
          icon={<FolderIcon />} 
          iconPosition="start"
          label="Veld Monsters"     
          sx={{ minHeight: 48 }}
        />
        <Tab 
          icon={<FolderIcon />} 
          iconPosition="start"
          label="Analysemonsters"     
          sx={{ minHeight: 48 }}
        />
        <Tab 
          icon={<FolderIcon />} 
          iconPosition="start"
          label="Analyses"     
          sx={{ minHeight: 48 }}
        />
      </Tabs>

      
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        bgcolor: 'background.default'
      }}>
        {selectedTab === 0 && (
          <ProjectInfoTab document={document} autoSave isReadOnly={isReadOnly} />
        )}
        {selectedTab === 1 && (
          <DrillPointsTab document={document} isReadOnly={isReadOnly} />
        )}
        {selectedTab === 2 && (           // <-- NEW Filter Tab Content
          <FilterTab document={document} isReadOnly={isReadOnly} />
        )}
        {/* {selectedTab === 3 && (           // Index shifted from 2 to 3
          <DrillMasterTab document={document} />
        )} */}
        {selectedTab === 3 && (           // Index shifted from 4 to 3
          <LayerInfo document={document} isReadOnly={isReadOnly} />
        )}
        {selectedTab === 4 && (           // Index shifted from 5 to 4
          <SampleTab document={document} isReadOnly={isReadOnly} />
        )}
        {selectedTab === 5 && (           // Index shifted from 6 to 5
          <AnalysemonsterTab document={document} isReadOnly={isReadOnly} />
        )}
        {selectedTab === 6 && (           // Index shifted from 7 to 6
          <Analysis_tab document={document} isReadOnly={isReadOnly} />
        )}
      </Box>
    </>
  );
});

// Resizable divider component
const ResizableDivider = memo(({ onDragStart, isDragging }) => {
  return (
    <Box
      sx={{
        width: '12px',
        height: '100%',
        backgroundColor: isDragging ? 'primary.light' : 'divider',
        cursor: 'col-resize',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        transition: 'background-color 0.2s',
        '&:hover': {
          backgroundColor: 'primary.main',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        position: 'relative',
      }}
      onMouseDown={onDragStart}
      role="separator"
      aria-orientation="vertical"
    >
      <Box 
        sx={{ 
          width: '4px', 
          height: '50px', 
          backgroundColor: isDragging ? 'background.paper' : 'grey.400',
          borderRadius: '2px',
        }} 
      />
    </Box>
  );
});

const DocumentViewer = ({ document, onClose }) => {
  const { user } = useContext(AuthContext);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.3); // Base rendering scale
  const zoomTimeoutRef = useRef(null); // Alleen nog voor de scroll correctie timing
  const [currentZoomLevel, setCurrentZoomLevel] = useState(ZOOM_LEVELS.indexOf(1.3) || 5);
  const [fileUrl, setFileUrl] = useState(null);
  const [error, setError] = useState(null);
  const [, setLoadedPages] = useState({});
  const pdfContainerRef = useRef(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [leftPanelWidth, setLeftPanelWidth] = useState(600);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const currentWidthRef = useRef(leftPanelWidth);
  const leftPanelPaperRef = useRef(null); // Ref for the Paper element
  const rafRef = useRef(null); // Ref for requestAnimationFrame ID
  const isDraggingRef = useRef(false); // Ref to track dragging status synchronously
  const [pdfDocument, setPdfDocument] = useState(null);
  const pdfDocumentRef = useRef(null);
  const [, setRenderedPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditingPage, setIsEditingPage] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [loadingProgress, setLoadingProgress] = useState({ processed: 0, total: 0, percentage: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [, setLoadedPagesSet] = useState(new Set());
  const [visiblePageRange, setVisiblePageRange] = useState({ startIndex: 1, endIndex: 5 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);

  const searchTimeoutRef = useRef(null);
  const searchResultsRef = useRef([]);
  const textContentCacheRef = useRef({});
  const [originalDimensionsData, setOriginalDimensionsData] = useState({});
  const zoomScrollTargetRef = useRef(null);
  const needsScrollCorrection = useRef(false);
  const [isReviewing, setIsReviewing] = useState(false); // State for review loading
  const [loadingMessage] = useState([
    "Heb je al koffie gehaald? ☕",
    "Vergeet geen water te drinken! 💧", 
    "Is het al bijna weekend? 🎉",
    "Werknemer van de maand hierzo! 🏆"
  ][Math.floor(Math.random() * 4)]);
  const [isDocumentExported, setIsDocumentExported] = useState(false); // New state

  // Nieuw: state voor dialog
  const [isNotesOpen, setIsNotesOpen] = useState(false); // Nieuw: state voor dialog
  const [notes, setNotes] = useState(document?.Opmerkingen || ''); // Init met bestaande opmerking
  const [noteBoxPos, setNoteBoxPos] = useState({ x: 100, y: 100 });
  const [isDraggingNote, setIsDraggingNote] = useState(false);
  const noteDragOffset = useRef({ x: 0, y: 0 });
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isDrawingToolOpen, setIsDrawingToolOpen] = useState(false); // State voor intekentool

  // Remember scroll position and zoom point for smooth zooming
  
  // Worker for background processing
  const workerRef = useRef(null);

  const handlePageAndRangeChange = useCallback((page, range) => {
    setCurrentPage(page);
    if (range) {
      setVisiblePageRange(range);
    }
  }, []);

  const handleTabChange = (_event, newValue) => {
    setSelectedTab(newValue);
  };

  // Effect to fetch file URL when document or user ID changes
  useEffect(() => {
    const fetchFileUrl = async () => {
      try {
        if (!user) {
          throw new Error('Authentication required');
        }

        if (!document?.storage_path) {
          throw new Error('No storage path provided');
        }

        // First try to get a signed URL which has temporary access
        const { data, error: signedError } = await supabase.storage
          .from('project-documents')
          .createSignedUrl(document.storage_path, 3600); // 1 hour expiry

        if (signedError) {
          console.error('Signed URL error:', signedError);
          // Fallback to download URL if signed URL fails
          const { data: downloadData } = await supabase.storage
            .from('project-documents')
            .download(document.storage_path);

          if (downloadData) {
            const url = URL.createObjectURL(downloadData);
            console.log('Using blob URL:', url);
            setFileUrl(url);
            return;
          }
          throw signedError;
        }

        if (data?.signedUrl) {
          setFileUrl(data.signedUrl);
          setError(null);
        } else {
          throw new Error('No URL generated');
        }
      } catch (err) {
        console.error('Error fetching file URL:', err);
        setError(`Failed to load document: ${err.message}`);
      }
    };

    fetchFileUrl();

    // Cleanup function to revoke object URLs
    return () => {
      if (fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, user?.id]); // Fetch when document or user ID changes

  // Effect to set isDocumentExported based on document status
  useEffect(() => {
    if (document && document.status === 'exported') {
      setIsDocumentExported(true);
    } else {
      setIsDocumentExported(false);
    }
  }, [document]);

  // Use a worker for text content processing
  useEffect(() => {
    initializeWorker();

    // Cleanup worker on component unmount
    return () => {
      if (workerRef.current) {
        // console.log('[DocumentViewer] Terminating worker on unmount');
        workerRef.current.terminate();
        if (window._pdfWorkerRegistry) {
          window._pdfWorkerRegistry.delete(workerRef.current);
        }
        workerRef.current = null;
      }
    };
  }, []); // Run only once on mount

  const initializeWorker = () => {
    if (window.Worker) {
      try {
        // First terminate any existing worker
        if (workerRef.current) {
          workerRef.current.terminate();
          
          // Remove from registry if it exists
          if (window._pdfWorkerRegistry) {
            window._pdfWorkerRegistry.delete(workerRef.current);
          }
        }
        
        // Create a new worker
        workerRef.current = new Worker(new URL('/pdfProcessingWorker.js', import.meta.url));
        
        // Register in global registry
        if (window._pdfWorkerRegistry) {
          window._pdfWorkerRegistry.add(workerRef.current);
        } else {
          window._pdfWorkerRegistry = new Set([workerRef.current]);
        }
        
        workerRef.current.onmessage = (e) => {
          const { type, data } = e.data;
          
          switch(type) {
            case 'progress':
              setLoadingProgress(data);
              break;
            case 'complete':
              setIsLoading(false);
              setRenderedPageCount(data.uniquePageCount);
              break;
            case 'fontWarning':
              console.debug('Font warning:', data.message);
              break;
            case 'fontIssueHandled':
              console.debug('Font issue handled for page:', data.pageNumber);
              break;
          }
        };
        
        // Add error handler for worker errors
        workerRef.current.onerror = (error) => {
          // Check if this is a font error
          if (error.message && 
              (error.message.includes('TT: undefined function:') || 
               error.message.includes('glyf table') ||
               error.message.includes('font'))) {
            // Suppress font errors to avoid crashing
            console.debug('Suppressed font error:', error.message);
            return;
          }
          
          console.error('Worker error:', error.message);
        };
      } catch (error) {
        console.error('Error initializing web worker:', error);
      }
    }
  };

  // Update the onDocumentLoadSuccess function
  const onDocumentLoadSuccess = useCallback((pdf) => {
    if (!pdf) {
      console.error('PDF document failed to load');
      setError('Failed to load PDF document');
      setIsLoading(false);
      return;
    }
  
    setPdfDocument(pdf);
    pdfDocumentRef.current = pdf;
    setNumPages(pdf.numPages);
    setIsLoading(true);
    setLoadingProgress({
      processed: 0,
      total: pdf.numPages,
      percentage: 0
    });
  
    // Reset all loading-related states
    pageCache.clear();
    setLoadedPagesSet(new Set());
    setRenderedPageCount(0);
    textContentCacheRef.current = {}; // Wis de tekstcache bij nieuw document
  
    // Process pages in batches
    const processPagesInBatches = async () => {
      const batchSize = 5;
      const pages = [];
  
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          pages.push({
            pageNumber: pageNum,
            content: textContent.items.map(item => item.str).join(' ')
          });
  
          // Send batch to worker when ready
          if (pages.length === batchSize || pageNum === pdf.numPages) {
            if (workerRef.current) {
              workerRef.current.postMessage({
                type: 'processPages',
                data: {
                  pages: [...pages],
                  total: pdf.numPages,
                  currentBatch: Math.ceil(pageNum / batchSize),
                  totalBatches: Math.ceil(pdf.numPages / batchSize)
                }
              });
            }
            pages.length = 0; // Clear the batch
          }
        } catch (error) {
          console.error(`Error processing page ${pageNum}:`, error);
        }
      }
    };
  
    processPagesInBatches();
  }, []);

  // Update the handlePageLoad function
  const handlePageLoad = useCallback((pageNumber, page) => {
    setLoadedPages(prev => {
      const newPages = { ...prev, [pageNumber]: true };
      
      // Calculate total loaded pages
      const loadedCount = Object.values(newPages).filter(Boolean).length;
      
      // Update loading progress
      setLoadingProgress(() => ({
        processed: loadedCount,
        total: numPages || 0,
        percentage: Math.round((loadedCount / (numPages || 1)) * 100)
      }));
  
      // Check if all pages are loaded
      if (loadedCount === numPages) {
        setIsLoading(false);
      }
  
      return newPages;
    });
  
    setLoadedPagesSet(prev => {
      const newSet = new Set(prev);
      if (!newSet.has(pageNumber)) {
        newSet.add(pageNumber);
        setRenderedPageCount(count => count + 1);
      }
      return newSet;
    });
  }, [numPages]);

  // Scrolls to a specific page and handles PDF rendering
  const scrollToPage = useCallback((pageIndex) => {
    const pageNumber = pageIndex + 1;
    
    if (pdfContainerRef.current) {
      const pageElement = pdfContainerRef.current.querySelector(`[data-page="${pageNumber}"]`)
        || pdfContainerRef.current.querySelector(`.react-pdf__Page[data-page-number="${pageNumber}"]`)
        || document.getElementById(`page_${pageNumber}`);
        
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        console.warn(`Page element ${pageNumber} not found for scrolling`);
      }
    }
  }, []);

  // Simplified zoom function focusing on correct state update and scroll targeting
  const handleZoom = useCallback((delta) => {
    if (!pdfContainerRef.current || !numPages /* || pageHeightsData.length < 1 */) { // Check pageHeightsData verwijderd
        console.warn('[handleZoom] Aborting: Missing container or numPages.');
        return;
    }

    const container = pdfContainerRef.current;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const pageMargin = 20; 

    // --- Determine central page & relative offset using ORIGINAL dimensions * current scale ---
    // Dit gebeurt nu met de *originele* dimensies die we hebben opgeslagen.
    let centralPageNumber = 1;
    let centralPageTop = 0;
    let centralPageHeight = 0;
    let minDistance = Infinity;
    const viewCenterY = scrollTop + clientHeight / 2;
    let accumulatedHeight = 0;
    
    // Check of we de originele data al hebben
    const hasOriginalData = Object.keys(originalDimensionsData).length > 0;

    for (let i = 0; i < numPages; i++) {
      const pageNum = i + 1;
      let originalHeight = 800; // Default fallback
      if (hasOriginalData && originalDimensionsData[pageNum]?.height) {
          originalHeight = originalDimensionsData[pageNum].height;
      }
      // Bereken de *huidige* geschaalde hoogte voor de check
      const currentScaledHeight = originalHeight * scale;

      const pageTop = accumulatedHeight;
      const pageCenterY = pageTop + currentScaledHeight / 2;
      const distance = Math.abs(viewCenterY - pageCenterY);

      if (distance < minDistance) {
        minDistance = distance;
        centralPageNumber = pageNum;
      }
      accumulatedHeight += currentScaledHeight + pageMargin;
    }

    // Bereken relatieve offset o.b.v. originele hoogte
    let originalCentralHeight = 800;
    if (hasOriginalData && originalDimensionsData[centralPageNumber]?.height) {
        originalCentralHeight = originalDimensionsData[centralPageNumber].height;
    }
    const currentScaledCentralHeight = originalCentralHeight * scale;
    // Herbereken top positie van de central page voor offset berekening
    let tempAccumulatedHeight = 0;
    for(let i = 0; i < centralPageNumber - 1; i++) {
        let h = 800;
        if(hasOriginalData && originalDimensionsData[i+1]?.height) h = originalDimensionsData[i+1].height;
        tempAccumulatedHeight += (h * scale) + pageMargin;
    }
    const currentCentralPageTop = tempAccumulatedHeight;
    const relativeOffsetRatio = currentScaledCentralHeight > 0 ? (viewCenterY - currentCentralPageTop) / currentScaledCentralHeight : 0.5;
    
    // Verwijderd: console.log(`[handleZoom] Determined central page...`);
    // --- End determination ---

    // Determine new zoom level index
    let newZoomIndex = currentZoomLevel;
    if (delta > 0 && newZoomIndex < ZOOM_LEVELS.length - 1) {
      newZoomIndex++;
    } else if (delta < 0 && newZoomIndex > 0) {
      newZoomIndex--;
    } else {
      return; // Already at min/max zoom
    }

    const newScale = ZOOM_LEVELS[newZoomIndex];

    // Store target for the useEffect hook
    zoomScrollTargetRef.current = { pageNumber: centralPageNumber, ratio: relativeOffsetRatio };
    needsScrollCorrection.current = true;

    // Update scale state (triggers re-render and useEffect)
    setCurrentZoomLevel(newZoomIndex);
    setScale(newScale);

  }, [
    currentZoomLevel, 
    scale, 
    numPages, 
    originalDimensionsData
]);

// Effect hook to handle scroll correction AFTER scale and dimensions are stable
useEffect(() => {
  if (
    needsScrollCorrection.current &&
    zoomScrollTargetRef.current &&
    pdfContainerRef.current &&
    Object.keys(originalDimensionsData).length > 0
  ) {
    const container = pdfContainerRef.current;
    const { pageNumber, ratio } = zoomScrollTargetRef.current;
    const pageIndex = pageNumber - 1;
    const pageMargin = 20;
    const clientHeight = container.clientHeight;

    // Verwijderd: console.log(`[useEffect/ScrollCorrection] Triggered...`);

    // --- Recalculate page top based on ORIGINAL dimensions and NEW scale ---
    let newCentralPageTop = 0;
    let newAccumulatedHeight = 0;
    const averageOriginalHeight = 800;

    for (let i = 0; i < pageIndex; i++) {
      const pageNum = i + 1;
      let originalH = averageOriginalHeight;
      if (originalDimensionsData[pageNum]?.height) {
          originalH = originalDimensionsData[pageNum].height;
      }
      const scaledH = originalH * scale; // Schaal met de *nieuwe* schaal
      newAccumulatedHeight += scaledH + pageMargin;
    }
    newCentralPageTop = newAccumulatedHeight;
    
    let originalTargetHeight = averageOriginalHeight;
     if (originalDimensionsData[pageNumber]?.height) {
          originalTargetHeight = originalDimensionsData[pageNumber].height;
      }
    const newCentralPageHeight = originalTargetHeight * scale; // Geschaalde hoogte van doelpagina
    // --- End recalculation ---

    // Calculate the target scrollTop
    const targetScrollTop = newCentralPageTop + (ratio * newCentralPageHeight) - (clientHeight / 2);
    
    // Verwijderd: console.log(`[useEffect/ScrollCorrection] Calculated New Page Top...`);

    requestAnimationFrame(() => {
        if (pdfContainerRef.current) {
             pdfContainerRef.current.scrollTop = Math.max(0, targetScrollTop);
        }
    });

    needsScrollCorrection.current = false;
    zoomScrollTargetRef.current = null;
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [scale, originalDimensionsData]); 

  // Optimize resize using requestAnimationFrame
  const updateWidth = useCallback((clientX) => {
    // Calculate new width based on mouse position and starting position
    const delta = clientX - dragStartXRef.current;
    const newWidth = Math.max(300, Math.min(1200, currentWidthRef.current + delta));
    
    // Update the width using RAF for smooth animation
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      setLeftPanelWidth(newWidth);
      
      // Update debounced width with delay to avoid excessive re-renders
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedWidth(newWidth);
        currentWidthRef.current = newWidth; // Update ref after debounce
      }, 100); // 100ms debounce
    });
  }, []);

  // Use a stable drag handler
  const handleDrag = useCallback((e) => {
    if (!isDraggingRef.current) return; // Check the ref instead of state
    e.preventDefault(); // Prevent text selection during drag
    
    // Calculate new width directly
    const delta = e.clientX - dragStartXRef.current;
    // Set a fixed maximum width of 1300px as seen in the photo
    const newWidth = Math.max(300, Math.min(1200, currentWidthRef.current + delta)); 
    
    // Cancel previous frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    // Directly update style using RAF
    rafRef.current = requestAnimationFrame(() => {
      if (leftPanelPaperRef.current) {
        leftPanelPaperRef.current.style.width = `${newWidth}px`;
      }
    });
  }, []);
  
  // Use a stable drag end handler
  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return; // Check ref

    isDraggingRef.current = false; // Update ref immediately

    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Calculate final width one last time
    // Note: Using the last known mouse position might be tricky if it's not available here.
    // We rely on the last width applied via style or calculate based on the initial width and last delta.
    // For simplicity, let's read the width directly from the style we set.
    let finalWidth = leftPanelWidth; // Default to current state
    if (leftPanelPaperRef.current) {
       const currentStyleWidth = parseInt(leftPanelPaperRef.current.style.width, 10);
       if (!isNaN(currentStyleWidth)) {
          finalWidth = currentStyleWidth;
       } else {
          // Fallback if style wasn't set or parsed correctly
          // Recalculate based on the last known info (might be slightly off if mouseup event clientX isn't reliable)
          // const lastDelta = ? ; // Hard to get last e.clientX reliably here
          // finalWidth = Math.max(300, Math.min(1200, currentWidthRef.current + lastDelta));
          // Sticking to reading style is safer
       }
    }

    // Update the actual React state *once* at the end
    setLeftPanelWidth(finalWidth);

    setIsDragging(false);
    window.document.removeEventListener('mousemove', handleDrag);
    window.document.removeEventListener('mouseup', handleDragEnd);
    window.document.body.style.cursor = ''; // Reset cursor
  }, [handleDrag, leftPanelWidth]); // Removed isDragging state dependency
  
  // Handle the drag start with optimized approach
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    // Store initial x position and current width for delta calculations
    dragStartXRef.current = e.clientX;
    currentWidthRef.current = leftPanelWidth; // Use the state variable directly
    
    setIsDragging(true);
    isDraggingRef.current = true; // Update ref immediately
    // Change cursor for entire page during drag
    window.document.body.style.cursor = 'col-resize';
    
    window.document.addEventListener('mousemove', handleDrag);
    window.document.addEventListener('mouseup', handleDragEnd);
  }, [leftPanelWidth, handleDrag, handleDragEnd]);
  
  // Clean up all timers and event listeners on unmount
  useEffect(() => {
    // Ensure listeners are removed if component unmounts while dragging
    return () => {
      // if (requestAnimationRef.current) { // REMOVE
      //   cancelAnimationFrame(requestAnimationRef.current);
      // }
      // if (debounceTimerRef.current) { // REMOVE
      //   clearTimeout(debounceTimerRef.current);
      // }
      // Check if dragging was inadvertently left active
      if (isDraggingRef.current) { // Check ref here too for cleanup safety
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        window.document.removeEventListener('mousemove', handleDrag);
        window.document.removeEventListener('mouseup', handleDragEnd);
        window.document.body.style.cursor = '';
      }
    };
  }, [handleDrag, handleDragEnd]); // No longer depends on isDragging state
  
  // Nieuwe handler voor interne links
  const handleInternalLinkClick = useCallback(({ pageNumber }) => {
    const currentPdfDoc = pdfDocumentRef.current;
    if (currentPdfDoc && pageNumber && pageNumber > 0 && pageNumber <= currentPdfDoc.numPages) {
      scrollToPage(pageNumber - 1); // scrollToPage verwacht 0-based index
    } else {
      console.warn(`Invalid internal link destination: ${pageNumber}`);
    }
  }, [scrollToPage]);

  // Memoize the PDF content to prevent re-renders during resizing
  const pdfContent = useMemo(() => {
    if (!fileUrl) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography>Loading document URL...</Typography>
        </Box>
      );
    }

    return (
      <Document
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={(error) => {
          console.error('PDF load error:', error);
            setError(`Failed to load PDF: ${error.message}`);
          }}
          options={RENDER_OPTIONS}
          onItemClick={handleInternalLinkClick}
          loading={
            <div style={{ width: '100%', height: '100%' }} />
          }
          error={
            <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">Failed to load PDF. Please try again later.</Typography>
            </Box>
          }
          >
          <VirtualizedPDF
            numPages={numPages}
            pdfDocument={pdfDocument}
            scale={scale}
            onPageLoad={handlePageLoad}
            onPageChange={(page, range) => handlePageAndRangeChange(page, range)}
            searchQuery={searchQuery}
            currentSearchResult={searchResults[currentSearchIndex]}
            onOriginalDimensionsUpdate={setOriginalDimensionsData}
          />
      </Document>
    );
  }, [fileUrl, numPages, scale, pdfDocument, onDocumentLoadSuccess, handlePageLoad, handlePageAndRangeChange, searchQuery, searchResults, currentSearchIndex, handleInternalLinkClick]);

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) { // Only allow digits
      setPageInputValue(value);
    }
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const pageNum = parseInt(pageInputValue, 10);
      if (pageNum && pageNum > 0 && pageNum <= numPages) {
        setCurrentPage(pageNum);
        scrollToPage(pageNum - 1);
      } else {
        setPageInputValue(currentPage.toString());
      }
      setIsEditingPage(false);
    } else if (e.key === 'Escape') {
      setPageInputValue(currentPage.toString());
      setIsEditingPage(false);
    }
  };

  const handlePageInputBlur = () => {
    setPageInputValue(currentPage.toString());
    setIsEditingPage(false);
  };

  // Zoekfunctionaliteit - pas aan om index op te slaan
  const handleSearch = useCallback(async (query) => {
    if (!pdfDocument) { 
      console.warn('Search aborted: PDF document not available.');
      setIsSearching(false);
      return;
    }

    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setSearchQuery(query); // Update query state even if empty
      setIsSearching(false); // Ensure searching stops if query is cleared
      return;
    }
    
    setIsSearching(true);
    setSearchQuery(query);
    searchResultsRef.current = [];
    
    // Maak de regex hier alvast, gebruik trimmedQuery
    const regex = new RegExp(trimmedQuery, 'gi');
    let pagesProcessedWithoutError = 0;

    for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
      try {
        let pageTextContentItems;

        // 1. Check de cache
        if (textContentCacheRef.current[pageNum]) {
          pageTextContentItems = textContentCacheRef.current[pageNum];
        } else {
          // 2. Niet in cache? Haal op uit PDF
          const page = await pdfDocument.getPage(pageNum);
          if (!page) {
            console.warn(`Skipping search for page ${pageNum}: Could not get page object.`);
            continue; // Skip to the next page
          }

          const textContent = await page.getTextContent();
          if (!textContent || !textContent.items) {
            console.warn(`Skipping search for page ${pageNum}: Could not get text content or items.`);
            textContentCacheRef.current[pageNum] = []; // Cache lege array bij fout
            continue; // Skip to the next page
          }
          pageTextContentItems = textContent.items;
          // 3. Sla op in cache voor volgende keer
          textContentCacheRef.current[pageNum] = pageTextContentItems;
        }

        // Nu zoeken we in de (gecachte) pageTextContentItems
        if (!pageTextContentItems || pageTextContentItems.length === 0) {
          continue; // Skip to the next page
        }

        pageTextContentItems.forEach((item, itemIndex) => { // Gebruik itemIndex hier direct
          if (!item || typeof item.str !== 'string') return; // Extra check for valid item
          
          let match;
          // Reset regex lastIndex before each new item string search
          regex.lastIndex = 0; 
          while ((match = regex.exec(item.str)) !== null) {
            // Voeg resultaten direct toe aan de ref array
            searchResultsRef.current.push({
              pageNumber: pageNum,
              text: match[0],
              itemIndex: itemIndex, // Gebruik de index uit forEach
              matchIndex: match.index,
              matchLength: match[0].length
            });
            // Prevent infinite loops with zero-length matches or patterns matching empty strings
            if (match.index === regex.lastIndex) {
              regex.lastIndex++;
            }
          }
        });

        pagesProcessedWithoutError++;

      } catch (error) {
        // Make error logging more specific
        console.error(`Error processing page ${pageNum} during search:`, error.message, error);
        // Optionally, decide whether to continue searching or stop
        // For now, we'll log and continue to the next page
        continue;
      }
    }
    
  
    
    // Definitieve state update na het doorzoeken van ALLE pagina's
    setSearchResults([...searchResultsRef.current]); 
    setCurrentSearchIndex(searchResultsRef.current.length > 0 ? 0 : -1); 
    setIsSearching(false);
    
    // Scroll naar het eerste resultaat als er resultaten zijn
    if (searchResultsRef.current.length > 0) {
      scrollToPage(searchResultsRef.current[0].pageNumber - 1);
    }

    // Log if some pages failed
    if (pagesProcessedWithoutError < pdfDocument.numPages) {
        console.warn(`Search completed, but ${pdfDocument.numPages - pagesProcessedWithoutError} pages could not be processed due to errors.`);
    }

  }, [pdfDocument, scrollToPage]); // currentSearchIndex verwijderd als dependency, niet meer nodig voor tussentijdse updates

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce de zoekactie
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(query);
    }, 300);
  };

  const handleNextResult = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    scrollToPage(searchResults[nextIndex].pageNumber - 1);
  };

  const handlePreviousResult = () => {
    if (searchResults.length === 0) return;
    const prevIndex = currentSearchIndex <= 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    scrollToPage(searchResults[prevIndex].pageNumber - 1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(-1);
    setIsSearching(false);
  };

  // Handler for the new 'Reviewed' button
  const handleReviewedClick = async () => {
    if (isDocumentExported) {
      alert("Dit document is geëxporteerd en kan niet meer als beoordeeld worden gemarkeerd of gewijzigd.");
      return;
    }

    if (!document || !user) {
      console.error("Document or user data missing.");
      alert("Kon status niet bijwerken: document- of gebruikersgegevens ontbreken.");
      return;
    }

    const confirmed = window.confirm("Heb je alles gecontroleerd? Hiermee wordt het document gemarkeerd als beoordeeld.");

    if (confirmed) {
      setIsReviewing(true); // Start loading indicator
      try {
        const { error: updateError } = await supabase
          .from('documents')
          .update({
            status: 'reviewed',
            reviewed_by: user.id
          })
          .eq('id', document.id);

        if (updateError) {
          throw updateError;
        }

        console.log(`Document ${document.id} marked as reviewed by user ${user.id}`);
        alert("Document succesvol gemarkeerd als beoordeeld.");

        // Call the onClose prop function provided by the parent
        if (onClose && typeof onClose === 'function') {
          onClose();
        } else {
          console.warn("onClose prop is missing or not a function. Viewer cannot be closed automatically.");
        }

        // Force a page reload after successful update and closing
        window.location.reload();

      } catch (error) {
        console.error('Error updating document status:', error);
        alert(`Fout bij het bijwerken van de documentstatus: ${error.message}`);
      } finally {
        setIsReviewing(false); // Stop loading indicator
      }
    }
  };

  // Nieuw: handlers voor notitie-dialog
  const handleOpenNotes = () => setIsNotesOpen(true);
  const handleCloseNotes = () => setIsNotesOpen(false);
  const handleNotesChange = (e) => setNotes(e.target.value);

  // Zet deze handlers BOVEN de render, NIET in de render-functie!
  // Drag handlers for note box
  const handleNoteMouseDown = (e) => {
    setIsDraggingNote(true);
    noteDragOffset.current = {
      x: e.clientX - noteBoxPos.x,
      y: e.clientY - noteBoxPos.y,
    };
  };
  const handleNoteMouseMove = useCallback((e) => {
    if (!isDraggingNote) return;
    setNoteBoxPos({
      x: e.clientX - noteDragOffset.current.x,
      y: e.clientY - noteDragOffset.current.y,
    });
  }, [isDraggingNote]);
  const handleNoteMouseUp = useCallback(() => {
    setIsDraggingNote(false);
  }, []);

  // Attach/detach drag listeners
  useEffect(() => {
    if (isDraggingNote) {
      window.addEventListener('mousemove', handleNoteMouseMove);
      window.addEventListener('mouseup', handleNoteMouseUp);
    } else {
      window.removeEventListener('mousemove', handleNoteMouseMove);
      window.removeEventListener('mouseup', handleNoteMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleNoteMouseMove);
      window.removeEventListener('mouseup', handleNoteMouseUp);
    };
  }, [isDraggingNote, handleNoteMouseMove, handleNoteMouseUp]);

  // Save note to Supabase
  const handleSaveNote = async () => {
    setIsSavingNote(true);
    try {
      const { error } = await supabase
        .from('documents')
        .update({ Opmerkingen: notes })
        .eq('id', document.id);
      if (error) throw error;
      setIsNotesOpen(false);
    } catch (err) {
      alert('Fout bij opslaan van opmerking: ' + err.message);
    } finally {
      setIsSavingNote(false);
    }
  };

  // Handler voor openen PDF in nieuwe tab
  const handleOpenPdfInNewTab = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  // Handler voor openen intekentool
  const handleOpenDrawingTool = () => {
    setIsDrawingToolOpen(true);
  };

  // Handler voor sluiten intekentool
  const handleCloseDrawingTool = () => {
    setIsDrawingToolOpen(false);
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Loading screen component
  const loadingScreen = (
    <Box 
      sx={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 3,
        bgcolor: 'background.paper',
        zIndex: 1000
      }}
    >
      <Box
        component="img"
        src="/logo192.png"
        sx={{
          width: 80,
          height: 80,
          animation: 'spin 2s linear infinite',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
      <Typography variant="h6" color="text.secondary">
        Document laden... {loadingProgress.percentage}%
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {loadingProgress.processed} van {loadingProgress.total} pagina's verwerkt
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {loadingMessage}
      </Typography>
    </Box>
  );

  // Update the return statement to include loading screen
  return (
    <Box sx={{
      display: 'flex',
      height: '100%',
      bgcolor: 'background.default',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Loading screen - placed outside the hidden container */}
      {isLoading && loadingScreen}
      
      {/* Main content - hidden while loading */}
      <Box sx={{
        width: '100%',
        height: '100%',
        visibility: isLoading ? 'hidden' : 'visible'
      }}>
        {/* Original content */}
        <Box sx={{
          display: 'flex',
          height: '100%',
          bgcolor: 'background.default',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Left Panel with Tabs */}
          <Paper
            ref={leftPanelPaperRef}
            sx={{
              width: `${leftPanelWidth}px`,
              height: '100%',
              overflow: 'hidden',
              borderRadius: 0,
              borderRight: 0,
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              position: 'relative',
              zIndex: 1,
              transition: isDragging ? 'none' : 'width 0.1s ease-out',
            }}
          >
            <InfoTabs 
              selectedTab={selectedTab} 
              handleTabChange={handleTabChange}
              document={document}
              isReadOnly={isDocumentExported}
              onOpenPdfInNewTab={handleOpenPdfInNewTab}
              onOpenDrawingTool={handleOpenDrawingTool}
            />
          </Paper>
          
          {/* Resizable Divider */}
          <ResizableDivider onDragStart={handleDragStart} isDragging={isDragging} />

          {/* Right Panel - PDF Viewer */}
          <Box sx={{
            flexGrow: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative' // Belangrijk voor absolute positionering
          }}>
            {/* Compacte Toolbar */}
            <Paper sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 0.5,
              borderRadius: 0,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              minHeight: 40,
              flexShrink: 0,
              gap: 2,
            }}>
              {/* Zoekbalk */}
              <TextField
                size="small"
                placeholder="Zoek in document..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                sx={{
                  width: 400,
                  '& .MuiOutlinedInput-root': {
                    height: 32,
                    fontSize: '0.875rem',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end" sx={{ gap: 0.5 }}>
                      <Typography variant="caption" sx={{ userSelect: 'none', whiteSpace: 'nowrap', mr: 0.5 }}>
                        {isSearching ? '...' : `${currentSearchIndex + 1}/${searchResults.length}`}
                      </Typography>
                      <IconButton size="small" onClick={handlePreviousResult} disabled={searchResults.length === 0 || isSearching}>
                        <NavigateBeforeIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleNextResult} disabled={searchResults.length === 0 || isSearching}>
                        <NavigateNextIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleClearSearch} disabled={isSearching}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ flexGrow: 1 }} />

              {/* Zoom controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" onClick={() => handleZoom(-1)} disabled={currentZoomLevel === 0}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ userSelect: 'none', minWidth: 45, textAlign: 'center', fontWeight: 500 }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <IconButton size="small" onClick={() => handleZoom(1)} disabled={currentZoomLevel === ZOOM_LEVELS.length - 1}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ height: 28 }} />

              {/* Paginanummer */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isEditingPage ? (
                  <TextField
                    size="small"
                    value={pageInputValue}
                    onChange={handlePageInputChange}
                    onKeyDown={handlePageInputKeyDown}
                    onBlur={handlePageInputBlur}
                    autoFocus
                    sx={{
                      width: 50,
                      '& .MuiOutlinedInput-root': {
                        height: 28,
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    onClick={() => {
                      setIsEditingPage(true);
                      setPageInputValue(currentPage.toString());
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
                      px: 1,
                      py: 0.5,
                      userSelect: 'none',
                      minWidth: 60,
                      textAlign: 'center',
                      fontWeight: 500,
                    }}
                  >
                    {currentPage} / {numPages || '?'}
                  </Typography>
                )}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ height: 28 }} />

              {/* Action buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Notitie knop */}
                <Tooltip title="Notitie">
                  <IconButton size="small" color="secondary" onClick={() => setIsNotesOpen(true)}>
                    <NoteAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Status knop */}
                <Tooltip title={
                  document.status === 'intake' ? 'Intake' :
                  document.status === 'to_review' ? 'Markeer als beoordeeld' :
                  document.status === 'reviewed' ? 'Beoordeeld' :
                  document.status === 'exported' ? 'Geëxporteerd' : 'Onbekend'
                }>
                  <span>
                    <IconButton
                      size="small"
                      color={
                        document.status === 'reviewed' ? 'success' :
                        document.status === 'to_review' ? 'primary' :
                        document.status === 'exported' ? 'info' : 'default'
                      }
                      disabled={isReviewing || isLoading}
                      onClick={document.status === 'to_review' ? handleReviewedClick : undefined}
                    >
                      {document.status === 'intake' && <HourglassEmptyIcon fontSize="small" />}
                      {document.status === 'to_review' && <CheckCircleIcon fontSize="small" />}
                      {document.status === 'reviewed' && <CheckCircleIcon fontSize="small" />}
                      {document.status === 'exported' && <CloudDoneIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Sluitknop */}
                <Tooltip title="Sluiten">
                  <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>

            {/* PDF Viewer - now uses virtualized content */}
            <Box 
              ref={pdfContainerRef}
              sx={{ 
                flexGrow: 1, 
                overflow: 'auto',
                bgcolor: 'grey.100',
                position: 'relative', 
                width: '100%',
                '& .react-pdf__Document': {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              {pdfContent}
              
              {/* Draggable Note Box */}
              {isNotesOpen && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: noteBoxPos.y,
                    left: noteBoxPos.x,
                    zIndex: 2000,
                    width: 500, // Vergroot van 350 naar 500px
                    bgcolor: 'background.paper',
                    boxShadow: 6,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    p: 3, // Vergroot padding van 2 naar 3
                    cursor: isDraggingNote ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    maxWidth: '90vw', // Responsive: maximaal 90% van viewport width
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2, // Iets meer ruimte
                      cursor: 'grab',
                    }}
                    onMouseDown={handleNoteMouseDown}
                  >
                    <NoteAddIcon sx={{ mr: 1, fontSize: 28 }} color="secondary" />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      Opmerking voor collega's
                    </Typography>
                    <IconButton size="small" onClick={() => setIsNotesOpen(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    multiline
                    minRows={6} // Meer rijen (was 4)
                    fullWidth
                    variant="outlined"
                    label="Opmerking"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Typ hier je opmerking voor je collega's..."
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setIsNotesOpen(false)} color="inherit">
                      Annuleren
                    </Button>
                    <Button
                      onClick={handleSaveNote}
                      color="primary"
                      variant="contained"
                      disabled={isSavingNote}
                    >
                      {isSavingNote ? 'Opslaan...' : 'Opslaan'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            
            {/* Status Bar */}
            <Paper sx={{ 
              py: 0.5,
              px: 2,
              borderRadius: 0,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              justifyContent: 'center',
              minHeight: 32,
              flexShrink: 0
            }}>
              <Typography variant="body2">
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Box>
      
      {/* Intekentool Overlay */}
      {isDrawingToolOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000
          }}
        >
          <DrawingTool 
            document={document}
            onClose={handleCloseDrawingTool}
            isReadOnly={document.status === 'exported'}
          />
        </Box>
      )}

      {/* Optioneel: Debug info */}
      {isNotesOpen && (
        <Box 
          sx={{ 
            position: 'fixed',
            top: 0,
            right: 0,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            p: 1,
            fontSize: '12px',
            zIndex: 9999
          }}>
          isNotesOpen: {String(isNotesOpen)}
        </Box>
      )}
    </Box>
  );
};

export default DocumentViewer;
